import type { FilterNode } from '@ofmos/shared/filter-ast';
import { INTAKE_REVIEWED_AT_FIELD, notYetReviewedFilter } from '@ofmos/shared/intake-reviewed';
import { RECORD_QUERY_MAX_LIMIT } from '@ofmos/shared/record-query';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { RecordRow } from '../../../data/client';
import { useListedRecords, useRecordCount, useRecordList } from '../../../data/hooks';
import { type NamedObject, newestFirst, selectOf, useNamedObject } from '../clients/clientData';

/**
 * THE INTAKES NOBODY HAS REVIEWED YET (register A6-12: "the button with a count opens a list of
 * exactly the counted records in the coaches' language, never a settings page"; ledger BE-INR's
 * request contract).
 *
 * One filter for both halves, "Reviewed at is empty" (`notYetReviewedFilter`), so the number on
 * the "Review intakes" button and the rows of the list it opens are the same set: the count is
 * the server's aggregate of that filter (`useRecordCount`), and the list asks for the same
 * aggregate with its first page (`withTotalCount`, DESIGN.md F16), never a count of loaded rows.
 * The importer's own "Needs review" is a different fact and is not read here.
 */
export const INTAKES_PATH = '/fulfillment/intakes';

/** The deliveries object and its "Reviewed at" field, or nothing on an installation without them. */
function useNotReviewed(): {
  readonly intake: NamedObject;
  readonly filter: FilterNode | undefined;
} {
  const intake = useNamedObject('intake_submission');
  const field = intake.fields.get(INTAKE_REVIEWED_AT_FIELD);
  const filter = useMemo(
    () => (field === undefined ? undefined : notYetReviewedFilter(field.id)),
    [field],
  );
  return { intake, filter };
}

/** The number for "Review intakes N", and whether it could be read. */
export interface IntakesToReviewCount {
  /** Zero while anything is missing, so the button does not show on an installation without intakes. */
  readonly count: number;
  /**
   * The count, or the metadata it needs, could not be read (DESIGN.md H7, "never show a failed
   * load as a zero"): the button stays without a number, beside the error line.
   */
  readonly isError: boolean;
  readonly retry: () => void;
}

/**
 * THE NUMBER FOR "REVIEW INTAKES N" (the Onboarding title block and the Dashboard, which the
 * Onboarding package owns).
 */
export function useIntakesToReviewCount(): IntakesToReviewCount {
  const { intake, filter } = useNotReviewed();
  const queryClient = useQueryClient();
  const label = intake.object?.labelIdentifierFieldId;
  const count = useRecordCount(intake.object?.id ?? '', {
    select: label === undefined || label === '' ? [] : [label],
    ...(filter === undefined ? {} : { filter }),
    enabled: filter !== undefined && label !== undefined && label !== '' && !intake.isPending,
  });
  /* A failed count asked again is back to pending without data in TanStack: still failed. */
  const failed = count.isError || (count.isPending && count.errorUpdateCount > 0);
  const { refetch } = count;
  return {
    count: count.data?.totalCount ?? 0,
    isError: intake.isError || failed,
    retry: () => {
      if (intake.isError) void queryClient.refetchQueries({ queryKey: ['objects'] });
      if (failed) void refetch();
    },
  };
}

export interface IntakesToReview {
  readonly intake: NamedObject;
  /** `absent` is an installation without the deliveries object or its "Reviewed at" field. */
  readonly status: 'loading' | 'absent' | 'error' | 'ready';
  readonly rows: readonly RecordRow[];
  /** The server's count of the whole filtered set, from the same request as the rows. */
  readonly total: number | null;
  readonly retry: () => void;
}

const NO_IDS: readonly string[] = [];
const LIST_FIELDS = ['name', 'agency', 'person', 'submitted_at', 'received_at', 'status'] as const;

/** The list itself: newest submission first, the whole set up to the query ceiling. */
export function useIntakesToReview(): IntakesToReview {
  const { intake, filter } = useNotReviewed();
  const objectId = intake.object?.id ?? '';
  const enabled = filter !== undefined && !intake.isPending;
  const orderBy = newestFirst(intake, 'submitted_at');
  const list = useRecordList(objectId, {
    select: selectOf(intake, LIST_FIELDS),
    ...(filter === undefined ? {} : { filter }),
    ...(orderBy === undefined ? {} : { orderBy }),
    limit: RECORD_QUERY_MAX_LIMIT,
    withTotalCount: true,
    enabled,
  });
  const listed = useListedRecords(objectId, enabled ? (list.data?.ids ?? NO_IDS) : NO_IDS, list);
  const queryClient = useQueryClient();
  /* A failed read asked again is back to pending without data in TanStack: still an error. */
  const retrying = list.isPending && list.errorUpdateCount > 0;
  /* The metadata failing is an error too, never "absent" (DESIGN.md H7; BR-FIX item 16). */
  const status: IntakesToReview['status'] = intake.isError
    ? 'error'
    : intake.isPending
      ? 'loading'
      : filter === undefined
        ? 'absent'
        : (list.isError && list.data === undefined) || retrying
          ? 'error'
          : list.isPending || listed.isPending
            ? 'loading'
            : 'ready';
  const { refetch } = list;
  return {
    intake,
    status,
    rows: enabled ? listed.records : [],
    total: list.data?.totalCount ?? null,
    retry: () => {
      if (intake.isError) void queryClient.refetchQueries({ queryKey: ['objects'] });
      else void refetch();
    },
  };
}
